import { plural } from '@ipfabric/frontend-shared-assets/utils/plural';
import { Button } from '@ipfabric/ui-components/Button/Button';
import { Card } from '@ipfabric/ui-components/Card/Card';
import { TableButton } from '@ipfabric/ui-components/TableButton/TableButton';
import { createTextColumn } from '@ipfabric/utils';
import { unsafeAssert } from '@ipfabric/utils/types/unsafeAssert';
import { createFrontendOnlyColumn } from 'components/Table/columns/frontendOnlyColumn';
import { Table } from 'components/Table/components/Table';
import type { TableBulkAction } from 'components/Table/model/TableModel';
import { useFrontendLocalTable } from 'components/Table/model/useFrontendLocalTable';
import { useMemo, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useWatch } from 'react-hook-form';
import type { AimApplicationDraft } from '../../../model/aimDrafts';
import { AimDataTransferManager } from '../dataTransfer/AimDataTransferManager';
import { AimApplicationDialog } from '../dialogs/AimApplicationDialog';
import { useAimOrphanDetection } from './helpers/useAimOrphanDetection';
import { useAimRowActions } from './helpers/useAimRowActions';

type Props = {
  form: UseFormReturn<{ aimApplications: AimApplicationDraft[] }>;
};

type ApplicationRow = AimApplicationDraft;

export const AimApplicationsTable = ({ form }: Props) => {
  const applications = useWatch({
    control: form.control,
    name: 'aimApplications',
  });

  const orphanDetection = useAimOrphanDetection(form as never);
  const [importExportOpen, setImportExportOpen] = useState(false);

  const actions = useAimRowActions<AimApplicationDraft>({
    fieldName: 'aimApplications' as never,
    control: form.control as never,
    setValue: form.setValue as never,
    isReferenced: (id) => [
      ...orphanDetection.applicationReferencingWorkloads(id),
    ],
    deleteConfirmLabel: (count) => ({
      header: `${count} ${plural(count, 'application')} will be deleted`,
      subHeader:
        count === 1
          ? 'Do you really want to delete this application?'
          : `Do you really want to delete ${count} selected applications?`,
      btnConfirm: 'Delete',
      btnSecondaryConfirm: 'Cancel',
    }),
    blockedDeleteLabel: (count, refs) => ({
      header: `Cannot delete: ${count} of selected applications ${plural(count, 'is', 'are')} referenced`,
      subHeader: `Referenced by ${refs} ${plural(refs, 'workload')}. Update or delete the referencing records first.`,
      btnConfirm: 'OK',
    }),
  });

  const tableData = applications;

  const columns = useMemo(
    () => [
      createTextColumn<ApplicationRow>({ key: 'name', title: 'Name' }),
      createTextColumn<ApplicationRow>({
        key: 'environment',
        title: 'Environment',
        renderContent: ({ row }: { row: ApplicationRow }) => ({
          content: row.environment ?? '—',
        }),
      }),
      createFrontendOnlyColumn<ApplicationRow>({
        key: 'actions',
        disabledSort: true,
        title: 'Actions',
        render: (row) => (
          <div className='flex flex-row items-center gap-xs'>
            <TableButton
              dataTestId={`aim-applications-${row.name}-edit-icon`}
              variant='plain'
              icon='fa-pen'
              size='small'
              onClick={() => actions.handleEdit(row)}
              type='button'
            />
            <TableButton
              dataTestId={`aim-applications-${row.name}-remove-icon`}
              variant='plain'
              accent='negative'
              icon='fa-trash-can'
              size='small'
              onClick={() => actions.handleDelete(row.id)}
              type='button'
            />
          </div>
        ),
      }),
    ],
    [actions],
  );

  const bulkActions: TableBulkAction[] = useMemo(
    () => [
      {
        key: 'delete',
        title: 'Delete',
        accent: 'negative',
        icon: 'fa-trash-can',
        disableAutoDeselect: true,
        onClick: async ({ selectedRows, deselectAll }) => {
          const ok = await actions.handleBulkDelete(
            unsafeAssert<Set<unknown>, Set<string>>(selectedRows),
          );
          if (ok) {
            deselectAll();
          }
        },
      },
    ],
    [actions],
  );

  const frontendSimpleFilter = useFrontendLocalTable({
    columns,
    data: tableData,
  });

  return (
    <Card className='flex flex-col flex-1 rounded-tl-none' noShadow>
      <Table
        borderless
        bulkActions={bulkActions}
        rowIdField='id'
        loadRowIdsForSelectAll='fromData'
        className='flex-1 bg-transparent'
        columns={columns}
        rowHeight={44}
        title='Application list'
        menuItems={[
          {
            standalone: true,
            elementWithKey: (
              <Button
                key='add-new'
                dataTestId='aim-applications-add'
                variant='outline'
                size='small'
                icon='fa-plus-circle'
                onClick={actions.handleAdd}
              >
                Add new
              </Button>
            ),
          },
          {
            standalone: true,
            elementWithKey: (
              <Button
                key='import-export'
                variant='outline'
                size='small'
                icon='fa-file-import'
                onClick={() => setImportExportOpen(true)}
              >
                Import data
              </Button>
            ),
          },
        ]}
        {...frontendSimpleFilter}
      />
      {actions.editedItem !== null && (
        <AimApplicationDialog
          formType={actions.editedItem === 'new' ? 'add' : 'edit'}
          initialValues={
            actions.editedItem === 'new' ? undefined : actions.editedItem
          }
          onClose={actions.handleCloseDialog}
          onSubmit={actions.handleSubmitDialog}
        />
      )}
      {actions.confirmDeleteDialogRenderer()}
      {importExportOpen && (
        <AimDataTransferManager
          triggeredFrom='applications'
          form={form as never}
          onClose={() => setImportExportOpen(false)}
        />
      )}
    </Card>
  );
};
